import React, {useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {i18n} from '@lingui/core';
import {t} from '@lingui/core/macro';
import {Trans} from '@lingui/react';
import cn from 'classnames';

import Button from 'shared/ui/Button/Button/Button';
import TableBox from 'shared/ui/Box/TableBox/TableBox';
import PlugNoAccess from 'shared/ui/Plugs/PlugNoAccess/PlugNoAccess';
import Tabs, {ITabItem} from 'shared/ui/Tabs/Tabs';
import {isTranslate} from 'shared/lib/lang';

import {
    getIncidentTabCount,
    INCIDENT_TAB_LABELS,
    INCIDENT_TAB_URLS,
    INCIDENT_TABS,
    IncidentTab,
    isIncidentTabAvailable,
    pickDefaultIncidentTab,
} from '../../lib/incidentTabs';
import {useIncidentTabsAccess} from '../../model/useIncidentTabsAccess';

import SiteIncidentsTab from './SiteIncidentsTab/SiteIncidentsTab';
import ServerIncidentsTab from './ServerIncidentsTab/ServerIncidentsTab';
import PortIncidentsTab from './PortIncidentsTab/PortIncidentsTab';
import DomainIncidentsTab from './DomainIncidentsTab/DomainIncidentsTab';

import styles from './LastIncidents.module.scss';

import type {ISummaryHome} from 'entities/monitor';

const TAB_CONTENT: Record<IncidentTab, React.FC> = {
    site: SiteIncidentsTab,
    server: ServerIncidentsTab,
    port: PortIncidentsTab,
    domain: DomainIncidentsTab,
};

interface ILastIncidentsProps {
    home?: ISummaryHome,
}

const LastIncidents: React.FC<ILastIncidentsProps> = ({home}) => {
    const navigate = useNavigate();
    const access = useIncidentTabsAccess();
    const [pickedTab, setPickedTab] = useState<IncidentTab | null>(null);
    const defaultTab = useRef<IncidentTab | null>(null);

    if (defaultTab.current === null && home) {
        defaultTab.current = pickDefaultIncidentTab(home);
    }

    const activeTab = pickedTab ?? defaultTab.current ?? 'site';

    const titles: Record<IncidentTab, string> = {
        site: t`Последние инциденты сайта`,
        server: t`Последние инциденты сервера`,
        port: t`Последние инциденты порта`,
        domain: t`Последние инциденты домена`,
    };

    const items: ITabItem<IncidentTab>[] = INCIDENT_TABS.map((id) => ({
        id,
        label: i18n._(INCIDENT_TAB_LABELS[id]),
        count: getIncidentTabCount(id, home),
    }));

    const ActiveTabContent = TAB_CONTENT[activeTab];

    return (
        <>
            <div className={styles.block}>
                <Tabs
                    items={items}
                    activeId={activeTab}
                    onChange={setPickedTab}
                    className={styles.tabs}
                />

                <TableBox className={styles.box}>
                    <div className={styles.header}>
                        <div className={cn(
                            styles.title,
                            styles[isTranslate() ? 'enColorTitle' : 'ruColorTitle'],
                        )}>
                            {titles[activeTab]}
                        </div>
                        <Button
                            variant="transparent"
                            onClick={() => navigate(INCIDENT_TAB_URLS[activeTab])}
                        >
                            <Trans id="home.button.table.incident" message="Все инциденты" />
                        </Button>
                    </div>

                    {
                        isIncidentTabAvailable(activeTab, home, access[activeTab])
                            ? <ActiveTabContent/>
                            : <PlugNoAccess size="sm"/>
                    }
                </TableBox>
            </div>
            <span className={styles.hiddenBlock} />
        </>
    );
};

export default LastIncidents;
